import { useState, useEffect } from 'react'
import axios from 'axios'
import { Link } from 'react-router-dom'
import InfiniteScroll from 'react-infinite-scroll-component'
import { ADD_ARTISTS, SET_ARTISTS } from '@/redux/features/music/searchSlice'
import { useDispatch, useSelector } from 'react-redux'
import TopBar from '@/components/navbar/TopBar'
import BottomBar from '@/components/sidebar/BottomBar'
import { Heart, Music, Play, Search } from 'lucide-react'
import SortFilter from '@/components/sortFilter/SortFilter'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import ArtistCardSkeleton from './ArtistCardSkeleton'
import "react-lazy-load-image-component/src/effects/blur.css";
import { debounce } from '@/utils/debounce';

const allSongsSortOptions = [
    {
        value: "default",
        label: "Most Recent",
    },
    {
        value: "likes",
        label: "Most Liked",
    },
    {
        value: "views",
        label: "Most Played",
    },
    {
        value: "songs",
        label: "Most Songs",
    }
]

function Artists() {
    const dispatch = useDispatch()
    const artists = useSelector(state => state.search.artists)
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)
    const { song } = useSelector(state => state.music)
    const [sort, setSort] = useState('default')
    const [term, setTerm] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [noResultsMsg, setNoResultsMsg] = useState(false);
    const [searchArtists, setSearchArtists] = useState([]);
    const { user } = useSelector(state => state.auth);

    useEffect(() => {
        fetchArtists()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, sort])

    // Search artist with debounce
    useEffect(() => {
        const debouncedSearch = debounce(async () => {
            if (!term) return;
            try {
                setIsLoading(true);
                const response = await axios.get(`/discover/artists?page=1&sort=default&limit=20&search=${term}`);
                setSearchArtists(response.data.artists || []);
                setIsLoading(false);
                if ((response.data.artists || []).length === 0) {
                    setNoResultsMsg(true);
                } else {
                    setNoResultsMsg(false);
                }
            } catch (error) {
                setIsLoading(false);
                console.error('Error fetching artists:', error);
            }
        }, 500);

        debouncedSearch();
        return () => debouncedSearch.cancel();
    }, [term]);

    const fetchArtists = async () => {
        try {
            if (page === 1) {
                dispatch(SET_ARTISTS([]))
            }
            const response = await axios.get(`/discover/artists?page=${page}&sort=${sort}&limit=20`)
            if (response.data.artists.length === 0 || response.data.artists.length < 10) {
                setHasMore(false)
            }

            dispatch(ADD_ARTISTS(response.data.artists))
        } catch (err) {
            console.error('Error fetching artists:', err)
        }
    }

    const handleSortChange = (sort, page) => {
        setSort(sort);
        setPage(page);
        setHasMore(true);
        dispatch(SET_ARTISTS([]));
    }

    const author = user?.username;

    const renderArtistCards = (artistsToRender) => {
        return artistsToRender.map((artist, i) => (
            <div key={i} className="flex flex-col items-center group pt-4 transition-all duration-300 truncate">
                <div className="relative mb-4">
                    <Link to={`/profile/${artist.username}`} className="block w-24 h-24 sm:w-28 sm:h-28 2xl:w-32 2xl:h-32 rounded-full overflow-hidden border-1 border-slate-700/50 dark:border-pink-300/50">
                        {artist.profile_url ? (
                            <LazyLoadImage effect="blur" src={artist.profile_url} alt={artist.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(artist.name)}&background=1F2937&color=fff`; }} />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900 dark:from-pink-200 dark:to-pink-300">
                                <span className="text-6xl font-bold text-green-400/80 dark:text-pink-400 font-potta hover:scale-110 transition-transform duration-300">
                                    {artist.name.charAt(0).toUpperCase()}
                                </span>
                            </div>
                        )}
                    </Link>
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-slate-800/90 dark:bg-pink-300/90 backdrop-blur px-3 py-1 rounded-full border border-slate-700/50 dark:border-pink-300/80 flex items-center gap-3 text-xs shadow-lg">
                        <div className="flex items-center gap-1">
                            <Music className="w-3 h-3 text-green-400 dark:text-black" />
                            <span className="text-slate-300 dark:text-slate-700">{artist?.stats?.total_songs >= 1000 ? (artist?.stats?.total_songs / 1000).toFixed(1) + 'k' : artist?.stats?.total_songs}</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <Heart className="w-3 h-3 text-green-400 fill-green-400 dark:text-pink-700 dark:fill-pink-700" />
                            <span className="text-slate-300 dark:text-pink-600">{artist?.stats?.total_likes >= 1000 ? (artist?.stats?.total_likes / 1000).toFixed(1) + 'k' : artist?.stats?.total_likes}</span>
                        </div>
                    </div>
                </div>
                <h3 className="text-center font-medium truncate w-full">
                    <Link to={author === artist.username ? `/profile` : `/profile/${artist.username}`} className="text-white dark:text-black hover:text-green-400 dark:hover:text-pink-700 transition-colors duration-500 text-xs 2xl:text-sm truncate w-full">
                        {artist.name}
                    </Link>
                </h3>
                <p className="text-slate-400 dark:text-slate-700 text-xs flex items-center gap-1 mt-1">
                    <Play className="w-3 h-3" />
                    <span>{artist?.stats?.total_views >= 1000 ? (artist?.stats?.total_views / 1000).toFixed(1) + 'k' : artist?.stats?.total_views} plays</span>
                </p>
            </div>
        ));
    };

    return (
        <div className={`min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 dark:from-pink-100 dark:via-pink-200 dark:to-pink-300 sm:px-4 px-2 py-8 ${song ? "pb-36 md:pb-28" : "pb-24 sm:pb-16"}`}>
            <TopBar heading={"Artists"} />
            <div className="mx-auto pt-16 w-full">
                {/* Header and Filter Section */}
                <div className="flex flex-col sm:flex-row items-start justify-between w-full mb-6 sm:px-0 px-2.5 gap-4">
                    <h1 className="text-2xl md:text-3xl font-bold font-comfortaa text-white dark:text-black tracking-tight relative inline-block">
                        Featured Artists
                    </h1>

                    <div className="flex items-center gap-1.5 sm:gap-3 w-full sm:w-auto">
                        <div className="relative group flex-grow sm:flex-grow-0 sm:min-w-[220px]">
                            <div className="relative flex items-center border border-slate-700/50 dark:border-pink-700/50 rounded-md bg-slate-800/70 backdrop-blur-sm overflow-hidden transition-all duration-300 group-hover:border-green-400/70">
                                <input type="text" placeholder="Search artists..." className="w-full bg-transparent text-white dark:text-black px-4 py-1 sm:py-2 focus:outline-none" value={term} onChange={(e) => setTerm(e.target.value)} />
                                <div className="px-3 text-slate-400 dark:text-pink-800 group-hover:text-green-400 dark:group-hover:text-pink-600 transition-colors duration-300">
                                    <Search size={20} className="group-hover:scale-110 transition-transform duration-300" />
                                </div>
                            </div>
                        </div>

                        {!term && (
                            <div className="animate-fadeIn">
                                <SortFilter setSort={handleSortChange} sortValue={sort} options={allSongsSortOptions} />
                            </div>
                        )}
                    </div>
                </div>

                {/* Artists Grid */}
                {!term && (
                    <InfiniteScroll
                        dataLength={artists.length}
                        next={() => setPage(prev => prev + 1)}
                        hasMore={hasMore}
                        loader={<ArtistCardSkeletons />}
                        className="grid grid-cols-3 xs:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 2xl:grid-cols-8 3xl:grid-cols-10 sm:gap-x-4 sm:gap-y-4 gap-x-2 gap-y-2"
                    >
                        {renderArtistCards(artists)}
                    </InfiniteScroll>
                )}

                {/* Search Results */}
                {term && !isLoading && searchArtists.length > 0 && (
                    <div className="grid grid-cols-3 xs:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 2xl:grid-cols-8 3xl:grid-cols-10 sm:gap-x-4 sm:gap-y-4 gap-x-2 gap-y-2">
                        {renderArtistCards(searchArtists)}
                    </div>
                )}

                {/* No Results Message */}
                {term && !isLoading && noResultsMsg && (
                    <div className="text-center py-8 text-slate-300 dark:text-pink-800">
                        No artists found matching &quot;{term}&quot;. Try a different search.
                    </div>
                )}

                {/* Loading State */}
                {term && isLoading && (
                    <div className="grid grid-cols-3 xs:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7 2xl:grid-cols-8 3xl:grid-cols-10 sm:gap-x-4 sm:gap-y-4 gap-x-2 gap-y-2">
                        <ArtistCardSkeletons />
                    </div>
                )}
            </div>
            <BottomBar />
        </div>
    )
}

function ArtistCardSkeletons() {
    return (
        <>
            <ArtistCardSkeleton />
            <ArtistCardSkeleton />
            <ArtistCardSkeleton />
            <ArtistCardSkeleton />
            <ArtistCardSkeleton />
            <ArtistCardSkeleton />
            <ArtistCardSkeleton />
            <ArtistCardSkeleton />
            <ArtistCardSkeleton />
            <ArtistCardSkeleton />
            <ArtistCardSkeleton />
            <ArtistCardSkeleton />
            <ArtistCardSkeleton />
        </>
    )
}

export default Artists